import { Box } from '@mui/material'
import { useCallback } from 'react'

import { useTabContext } from '@/context/TabContext/TabContext'
import { DetailHeaderWithHistory } from '@/pages/User/pages/Home/components/DetailHeader'

import { ElementTabsV2 } from './ElementTabsV2'
import { useAutosave, useGlobalKeyboardShortcuts } from './hooks'
import { ElementDetailV2Props } from './types'

/** Wait this long after the last change before calling save (debounced) */
const DEFAULT_AUTOSAVE_DELAY = 2000

/**
 * Generic wrapper component for element detail pages (V2 architecture).
 * 
 * Features:
 * - Unified header with breadcrumbs, title, and action buttons in one row
 * - View History accessible from the header menu
 * - Debounced autosave (no save/dirty indicator in the header)
 * - Global keyboard shortcuts (Ctrl+S)
 * - Tab navigation with keyboard support
 * 
 * @example
 * ```tsx
 * <ElementDetailV2
 *   tabs={viewTabs}
 *   selectedTab={selectedTab}
 *   onTabChange={setSelectedTab}
 *   editMode={editMode}
 *   dirty={dirty}
 *   node={node}
 *   onEdit={handleEdit}
 *   onCancel={handleCancel}
 *   onSave={handleSave}
 *   beforePush={handleBeforePush}
 *   autosaveDelay={2000}
 * />
 * ```
 */
export const ElementDetailV2 = <TTab extends string>({
	tabs,
	selectedTab,
	onTabChange,
	editMode,
	dirty,
	node,
	onEdit,
	onCancel,
	onSave,
	beforePush,
	autosaveDelay = DEFAULT_AUTOSAVE_DELAY,
	autosaveResetKey = 0,
	customActionContent,
	additionalMenuItems,
}: ElementDetailV2Props<TTab>) => {
	const { onSaveError } = useTabContext()

	const { cancel: cancelAutosave } = useAutosave({
		delay: autosaveDelay,
		enabled: editMode,
		dirty,
		onSave,
		onError: onSaveError,
		resetKey: autosaveResetKey,
	})

	// Global Ctrl+S shortcut
	useGlobalKeyboardShortcuts({
		enabled: editMode,
		onSave,
		onError: onSaveError,
		beforeSave: cancelAutosave,
	})

	// Before push, save all pending changes
	const handleBeforePush = useCallback(async () => {
		cancelAutosave()
		try {
			await onSave()
			await beforePush?.()
		} catch (e) {
			onSaveError(e)
			throw e
		}
	}, [onSave, beforePush, cancelAutosave, onSaveError])

	return (
		<Box className="flex flex-col h-full">
			<DetailHeaderWithHistory
				node={node}
				editMode={editMode}
				dirty={dirty}
				showEditSaveStatus={false}
				onEdit={onEdit}
				onCancel={onCancel}
				beforePush={handleBeforePush}
				additionalButtons={customActionContent}
				additionalMenuItems={additionalMenuItems}
			/>
			<ElementTabsV2
				tabs={tabs}
				selectedTab={selectedTab}
				onTabChange={onTabChange}
				editMode={editMode}
				dirty={dirty}
				node={node}
			/>
		</Box>
	)
}

